import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Loader2, Layers, Pencil, Trash2, Users, Smartphone, X, Search } from "lucide-react";
import { IconAction } from "@/components/IconAction";
import { InviteResultDialog } from "@/components/InviteResultDialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { DaysPicker } from "@/components/Timetable";

export default function ClassesPage() {
  const nav = useNavigate();
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editTarget, setEditTarget] = useState(null); // null | {} (new) | class
  const [form, setForm] = useState({ name: "", teacher_id: "", period: "", room: "", credits: "", start_time: "", end_time: "", days: ["Mon", "Tue", "Wed", "Thu", "Fri"] });
  const [saving, setSaving] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignQ, setAssignQ] = useState("");
  const [checked, setChecked] = useState({});
  const [rosterTarget, setRosterTarget] = useState(null);
  const [roster, setRoster] = useState([]);
  const [inviteResult, setInviteResult] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [teachTarget, setTeachTarget] = useState(null);
  const [teachForm, setTeachForm] = useState({ teacher_id: "", subject_id: "", days: [], start_time: "", end_time: "" });
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      const [c, o, s, sub] = await Promise.all([
        api.get("/classes"), api.get("/officers"), api.get("/students"), api.get("/subjects"),
      ]);
      setClasses(c.data);
      setTeachers(o.data.filter((p) => p.role === "teacher"));
      setStudents(s.data);
      setSubjects(sub.data);
      setTeachTarget((t) => (t ? c.data.find((x) => x.id === t.id) || null : t));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (c) => {
    setForm(c ? { name: c.name, teacher_id: c.teacher_id || "", period: c.period || "", room: c.room || "", credits: c.credits ? String(c.credits) : "", start_time: c.start_time || "", end_time: c.end_time || "", days: c.days || ["Mon", "Tue", "Wed", "Thu", "Fri"] } : { name: "", teacher_id: "", period: "", room: "", credits: "", start_time: "", end_time: "", days: ["Mon", "Tue", "Wed", "Thu", "Fri"] });
    setEditTarget(c || {});
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name: form.name, teacher_id: form.teacher_id || null, period: form.period, room: form.room, credits: Number(form.credits) || 0, days: form.days, start_time: form.start_time, end_time: form.end_time };
      if (editTarget?.id) await api.put(`/classes/${editTarget.id}`, payload);
      else await api.post("/classes", payload);
      toast.success("Grade saved");
      setEditTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!confirm(`Delete ${c.name}? Students will become unassigned.`)) return;
    try {
      await api.delete(`/classes/${c.id}`);
      toast.success("Grade deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const openAssign = (c) => {
    const map = {};
    students.forEach((s) => { map[s.id] = s.class_id === c.id || (s.class_ids || []).includes(c.id); });
    setChecked(map);
    setAssignQ("");
    setAssignTarget(c);
  };

  const saveAssign = async () => {
    setSaving(true);
    try {
      const ids = Object.keys(checked).filter((id) => checked[id]);
      await api.put(`/classes/${assignTarget.id}/students`, { student_ids: ids });
      toast.success(`${ids.length} students assigned to ${assignTarget.name}`);
      setAssignTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const openRoster = async (c) => {
    setRosterTarget(c);
    setRoster([]);
    try {
      const { data } = await api.get(`/classes/${c.id}/students`);
      setRoster(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const addAssignment = async (e) => {
    e.preventDefault();
    if (!teachForm.teacher_id || !teachForm.subject_id) return;
    setSaving(true);
    try {
      await api.post(`/classes/${teachTarget.id}/assignments`, teachForm);
      setTeachForm({ teacher_id: "", subject_id: "", days: [], start_time: "", end_time: "" });
      await load();
      toast.success("Teacher assigned");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const removeAssignment = async (a) => {
    try {
      await api.delete(`/class-assignments/${a.id}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const invite = async (student, contact, extra = {}) => {
    try {
      const { data } = await api.post("/parents/invite", {
        student_id: student.id, phone: contact.phone, name: contact.name, email: extra.email || contact.email || "", origin_url: window.location.origin,
      });
      setInviteResult({ contact: contact.name || contact.phone, _student: student, _contact: contact, ...data });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invite failed");
    }
  };

  const cql = q.trim().toLowerCase();
  const visibleClasses = classes.filter((c) =>
    !cql ||
    (c.name || "").toLowerCase().includes(cql) ||
    (c.teacher_name || "").toLowerCase().includes(cql) ||
    (c.assignments || []).some((a) => (a.teacher_name || "").toLowerCase().includes(cql) || (a.subject_name || "").toLowerCase().includes(cql))
  );

  return (
    <div className="p-6 md:p-10" data-testid="classes-page">
      <header className="mb-8 flex items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500">School</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Courses</h1>
          <p className="text-sm text-slate-600 mt-1">Homerooms and courses — assign a teacher and students; teachers can then take attendance, grade and message their course's parents.</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={() => openEdit(null)} data-testid="class-add-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:shadow transition">
            <Plus className="h-4 w-4" /> Add course
          </button>
        </div>
      </header>

      <section className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="classes-search-input"
              placeholder="Search course, teacher or subject…"
              className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
            />
          </div>
          {q.trim() && <div className="text-xs text-slate-500">{visibleClasses.length} of {classes.length} grades</div>}
        </div>
        <table className="w-full text-sm" data-testid="classes-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
              <th className="px-5 py-3">Course</th>
              <th className="px-5 py-3">Teacher</th>
              <th className="px-5 py-3">Students</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-500">Loading…</td></tr>}
            {!loading && classes.length === 0 && (
              <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-500">No classes yet. Click "Add class" — tip: add teachers first on the Employees page.</td></tr>
            )}
            {!loading && classes.length > 0 && visibleClasses.length === 0 && (
              <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-500">No classes match your search.</td></tr>
            )}
            {visibleClasses.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`class-row-${c.id}`}>
                <td className="px-5 py-3 font-medium text-slate-900">
                  <button
                    onClick={() => nav(`/admin/students?class_id=${c.id}`)}
                    data-testid={`class-name-link-${c.id}`}
                    title="View this course's students"
                    className="inline-flex items-center gap-2 hover:text-primary transition"
                  >
                    <Layers className="h-4 w-4 text-primary" /> {c.name}
                  </button>
                  {(c.period || c.room || c.credits || (c.days && c.days.length < 5)) && <div className="text-[11px] text-slate-500 mt-0.5 ml-6" data-testid={`class-period-room-${c.id}`}>{[c.period, c.start_time && `${c.start_time}–${c.end_time || "?"}`, c.room && `Room ${c.room}`, c.credits && `${c.credits} cr`, c.days && c.days.length < 5 && c.days.join("/")].filter(Boolean).join(" · ")}</div>}
                </td>
                <td className="px-5 py-3 text-slate-700">
                  <div>{c.teacher_name || <span className="text-slate-400">Unassigned</span>}
                    {c.teacher_name && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-slate-400">Homeroom</span>}
                  </div>
                  {c.assignments?.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {c.assignments.map((a) => (
                        <span key={a.id} className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700" data-testid={`class-assignment-chip-${a.id}`}>
                          {a.teacher_name} · {a.subject_name}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-5 py-3">
                  <button
                    onClick={() => nav(`/admin/students?class_id=${c.id}`)}
                    data-testid={`class-count-link-${c.id}`}
                    className="text-slate-700 hover:text-primary hover:underline transition"
                  >
                    {c.student_count}
                  </button>
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center justify-end gap-0.5">
                    <IconAction onClick={() => openRoster(c)} Icon={Smartphone} label="Parent invites" testid={`class-roster-${c.id}`} />
                    <IconAction onClick={() => openAssign(c)} Icon={Users} label="Assign students" testid={`class-assign-${c.id}`} />
                    <IconAction onClick={() => openEdit(c)} Icon={Pencil} label="Edit" tone="primary" testid={`class-edit-${c.id}`} />
                    <IconAction onClick={() => remove(c)} Icon={Trash2} label="Delete" tone="danger" testid={`class-delete-${c.id}`} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="sm:max-w-md" data-testid="class-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editTarget?.id ? "Edit class" : "Add class"}</DialogTitle>
            <DialogDescription>Give the course a name (e.g. 7th Grade Algebra or Grade 4 - Room B) and pick its teacher.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 pt-1">
            <label className="block">
              <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">Class name</span>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="class-name-input" placeholder="Grade 4 homeroom · or · Algebra I" className={inputCls} />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">Period <span className="normal-case tracking-normal text-slate-400">(optional)</span></span>
                <input value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} data-testid="class-period-input" placeholder="Period 1" className={inputCls} />
              </label>
              <label className="block">
                <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">Room <span className="normal-case tracking-normal text-slate-400">(optional)</span></span>
                <input value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} data-testid="class-room-input" placeholder="309" className={inputCls} />
              </label>
            </div>
            <label className="block">
              <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">Credits <span className="normal-case tracking-normal text-slate-400">(high school · optional)</span></span>
              <input type="number" min="0" max="20" step="0.5" value={form.credits} onChange={(e) => setForm({ ...form, credits: e.target.value })} data-testid="class-credits-input" placeholder="1" className={inputCls} />
              <span className="block mt-1 text-xs text-slate-500">Totalled on report cards when the Credits column is turned on in the report card format.</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">Bell · starts</span>
                <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="class-start-input" className={inputCls} />
              </label>
              <label className="block">
                <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">Bell · ends</span>
                <input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="class-end-input" className={inputCls} />
              </label>
            </div>
            <div>
              <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">Meets on</span>
              <DaysPicker value={form.days} onChange={(d) => setForm({ ...form, days: d })} testid="class-days" />
            </div>
            <label className="block">
              <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">Teacher</span>
              <select value={form.teacher_id} onChange={(e) => setForm({ ...form, teacher_id: e.target.value })}
                data-testid="class-teacher-select" className={inputCls}>
                <option value="">— No teacher yet —</option>
                {teachers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              {teachers.length === 0 && <span className="block mt-1 text-xs text-amber-700">No teachers yet — add one on the Employees page with the "Teacher" role.</span>}
            </label>
            <DialogFooter className="pt-1">
              <button type="submit" disabled={saving} data-testid="class-save-btn"
                className="h-10 rounded-lg bg-primary px-5 text-sm font-medium text-white inline-flex items-center gap-2 hover:shadow disabled:opacity-60 transition">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save class
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!assignTarget} onOpenChange={(o) => !o && setAssignTarget(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="class-assign-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Students in {assignTarget?.name}</DialogTitle>
            <DialogDescription>Search the school roster and tick every student enrolled in this class. Students can be in many classes (periods); their homeroom is set on the student form.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input autoFocus value={assignQ} onChange={(e) => setAssignQ(e.target.value)} placeholder="Search students by name or grade…" data-testid="assign-search"
                className="w-full h-10 rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-primary" />
            </div>
            <span className="shrink-0 text-xs font-semibold text-slate-600" data-testid="assign-selected-count">{Object.values(checked).filter(Boolean).length} selected</span>
          </div>
          <div className="max-h-80 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-lg">
            {(() => {
              const aq = assignQ.trim().toLowerCase();
              const list = students.filter((s) => !aq || s.full_name.toLowerCase().includes(aq) || (s.grade || "").toLowerCase().includes(aq))
                .sort((x, y) => (checked[y.id] ? 1 : 0) - (checked[x.id] ? 1 : 0) || x.full_name.localeCompare(y.full_name));
              if (list.length === 0) return <div className="p-6 text-center text-sm text-slate-400">{students.length === 0 ? "No students on the roster yet." : `No students match “${assignQ}”.`}</div>;
              return list.slice(0, 200).map((s) => {
                const other = s.class_id && s.class_id !== assignTarget?.id ? classes.find((c) => c.id === s.class_id)?.name : null;
                const isHome = s.class_id === assignTarget?.id;
                return (
                  <label key={s.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 cursor-pointer" data-testid={`assign-student-${s.id}`}>
                    <input type="checkbox" checked={!!checked[s.id]} onChange={(e) => setChecked({ ...checked, [s.id]: e.target.checked })} className="h-4 w-4 accent-[#1d4ed8]" />
                    <span className="text-sm text-slate-900 flex-1">{s.full_name} <span className="text-xs text-slate-400">{s.grade}</span></span>
                    {isHome && <span className="text-[10px] rounded-full bg-primary/10 px-2 py-0.5 text-primary font-semibold">Homeroom</span>}
                    {other && <span className="text-[10px] rounded-full bg-slate-100 px-2 py-0.5 text-slate-500">homeroom {other}</span>}
                  </label>
                );
              });
            })()}
          </div>
          <DialogFooter>
            <button onClick={saveAssign} disabled={saving} data-testid="assign-save-btn"
              className="h-10 rounded-lg bg-primary px-5 text-sm font-medium text-white inline-flex items-center gap-2 hover:shadow disabled:opacity-60 transition">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save assignment
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rosterTarget} onOpenChange={(o) => !o && setRosterTarget(null)}>
        <DialogContent className="sm:max-w-xl" data-testid="class-roster-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Parent app · {rosterTarget?.name}</DialogTitle>
            <DialogDescription>Invite each parent to the mobile app so they can receive and reply to messages.</DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-lg">
            {roster.map((s) => (
              <div key={s.id} className="px-4 py-3">
                <div className="text-sm font-medium text-slate-900">{s.full_name}</div>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {s.contacts.filter((c) => c.phone).map((c, i) => (
                    <button key={i} onClick={() => invite(s, c)} data-testid={`roster-invite-${s.id}-${i}`}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition ${
                        c.app_status === "active" ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : c.app_status === "invited" ? "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100"
                        : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
                      <Smartphone className="h-3 w-3" /> {c.name || c.phone} {c.app_status === "active" ? "· on app" : c.app_status === "invited" ? "· invited — resend" : "· invite"}
                    </button>
                  ))}
                  {s.contacts.filter((c) => c.phone).length === 0 && <span className="text-xs text-slate-400">No phone on file</span>}
                </div>
              </div>
            ))}
            {roster.length === 0 && <div className="p-6 text-center text-sm text-slate-400">No students in this class yet.</div>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!teachTarget} onOpenChange={(o) => !o && setTeachTarget(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="class-teachers-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Teachers of {teachTarget?.name}</DialogTitle>
            <DialogDescription>
              The homeroom teacher plus every subject teacher — all of them can take attendance and message this course's parents.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between rounded-lg bg-slate-50 border border-slate-200 px-4 py-2.5">
              <span className="text-sm text-slate-900">{teachTarget?.teacher_name || <span className="text-slate-400">No homeroom teacher</span>}</span>
              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Homeroom</span>
            </div>
            {(teachTarget?.assignments || []).map((a) => (
              <div key={a.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-2.5" data-testid={`assignment-row-${a.id}`}>
                <span className="text-sm text-slate-900">{a.teacher_name}
                  <span className="ml-2 inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700">{a.subject_name}</span>
                  {(a.days?.length > 0 || a.start_time) && (
                    <span className="ml-2 text-[11px] text-slate-500">
                      {(a.days || []).map((d) => ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][d]).join(", ")}
                      {a.start_time && ` · ${a.start_time}${a.end_time ? `–${a.end_time}` : ""}`}
                    </span>
                  )}
                </span>
                <button onClick={() => removeAssignment(a)} data-testid={`assignment-delete-${a.id}`} className="text-rose-500 hover:text-rose-700" title="Remove">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          <form onSubmit={addAssignment} className="space-y-2 pt-1 border-t border-slate-100 mt-1">
            <div className="flex flex-wrap gap-2">
              <select value={teachForm.teacher_id} onChange={(e) => setTeachForm({ ...teachForm, teacher_id: e.target.value })}
                data-testid="assignment-teacher-select" className={`${inputCls} flex-1 min-w-36`}>
                <option value="">Teacher…</option>
                {teachers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <select value={teachForm.subject_id} onChange={(e) => setTeachForm({ ...teachForm, subject_id: e.target.value })}
                data-testid="assignment-subject-select" className={`${inputCls} flex-1 min-w-32`}>
                <option value="">Subject…</option>
                {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex gap-1" data-testid="assignment-days">
                {["M", "T", "W", "T", "F", "S", "S"].map((label, d) => (
                  <button key={d} type="button"
                    onClick={() => setTeachForm((f) => ({ ...f, days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d] }))}
                    data-testid={`assignment-day-${d}`}
                    title={["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"][d]}
                    className={`h-8 w-8 rounded-full text-xs font-bold transition ${
                      teachForm.days.includes(d) ? "bg-primary text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                    }`}>
                    {label}
                  </button>
                ))}
              </div>
              <input type="time" value={teachForm.start_time} onChange={(e) => setTeachForm({ ...teachForm, start_time: e.target.value })}
                data-testid="assignment-start-time" className={`${inputCls} w-28`} />
              <span className="text-slate-400 text-xs">to</span>
              <input type="time" value={teachForm.end_time} onChange={(e) => setTeachForm({ ...teachForm, end_time: e.target.value })}
                data-testid="assignment-end-time" className={`${inputCls} w-28`} />
              <button type="submit" disabled={saving || !teachForm.teacher_id || !teachForm.subject_id} data-testid="assignment-add-btn"
                className="h-10 rounded-lg bg-primary px-4 text-sm font-medium text-white inline-flex items-center gap-1.5 hover:shadow disabled:opacity-50 transition ml-auto">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Assign
              </button>
            </div>
            <p className="text-[11px] text-slate-400">Days &amp; time are optional — they power the teacher's weekly schedule view.</p>
          </form>
          {subjects.length === 0 && (
            <p className="text-xs text-amber-700">No subjects yet — create them under Settings → Subjects first.</p>
          )}
        </DialogContent>
      </Dialog>

      <InviteResultDialog result={inviteResult} onClose={() => setInviteResult(null)}
        onResend={(extra) => invite(inviteResult._student, inviteResult._contact, extra)} />

    </div>
  );
}

const inputCls = "w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none";
